// src/components/Timetable.tsx
import { useUnsavedChanges } from "../../../../layout/AppShellContext";
import React, { useState, useEffect, useRef } from "react";
import { useSwipeable } from "react-swipeable";
import { apiCustom } from "../../../../api/client";
import { useActiveCompanyId } from "../../../../api/useActiveCompanyId";
import { useActiveCompanyIdApi } from "../../../../api/useActiveCompanyIdApi";
import { isIntentionalMonthSwipe } from "../../monthSwipe";
import { moscowCalendarDate } from "../../calendarDate";
import "../../monthNavigation.scss";
import "./timetable.scss";
import MonthPlanner from "../../../SchedulePage/MonthPlanner";

type Period = {
    from: string;
    to: string;
    actualOpen?: string | null;
    actualClose?: string | null;
    online: boolean;
};
type StaffSchedule = { staffId: number; staffName: string; day: Period[][] };
type TransferStaffOption = { id: number; name?: string | null };
type TransferRecord = {
    id: number;
    dateTime?: string | null;
    clientName?: string | null;
    staffName?: string | null;
    amount?: number;
    services?: string[];
};

type ApiResponse = {
    dayMonth: string[];
    staffSchedule: StaffSchedule[];
    monthArrayName: { alias: string; apiDate: string }[];
    activeMonth: { alias: string; apiDate: string };
    CompanyUserEnable: { id: number; address: string }[];
    SettingScheduleAll: boolean;
    SettingScheduleYourself: boolean;
};

type SelectedCell = { staffId: number; date: number }[];
type EditModalState = { open: boolean; cells: SelectedCell; periods: Period[] };
type TransferModalState = {
    open: boolean;
    cells: SelectedCell;
    periods: Period[];
    records: TransferRecord[];
    staffOptions: TransferStaffOption[];
    selectedStaffId: string;
    message: string;
};

const VISIBLE_ROWS = 6;

const CompactTimetable: React.FC<{ initialSelection?: { staffId: number; date: string }; onSaved?: () => void }> = ({ initialSelection, onSaved }) => {
    const initialStaffId = initialSelection?.staffId;
    const initialDate = initialSelection?.date;
    const { value: activeCompanyId, resolved: activeCompanyResolved } = useActiveCompanyId();
    const { value: activeCompanyIdApi, resolved: activeCompanyApiResolved } = useActiveCompanyIdApi();
    const [data, setData] = useState<ApiResponse | null>(null);
    const [active, setActive] = useState(0);
    const [selectedCells, setSelectedCells] = useState<SelectedCell>([]);
    const [editModal, setEditModal] = useState<EditModalState | null>(null);
    const [transferModal, setTransferModal] = useState<TransferModalState | null>(null);
    useUnsavedChanges(!!editModal || !!transferModal, "Изменение расписания");
    const [collapsed, setCollapsed] = useState(true);

    const todayRef = useRef<HTMLTableCellElement | null>(null);
    const initialApplied = useRef("");
    const requestVersion = useRef(0);
    const navigationBusy = useRef(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [monthLoading, setMonthLoading] = useState(false);

    const loadSchedule = async (monthApiDate?: string) => {
        const version = ++requestVersion.current;
        setLoadError(null);
        const url = monthApiDate
            ? `/lkfr/staff/date-schedule?date=${monthApiDate}`
            : "/lkfr/staff/date-schedule";

        let resp: ApiResponse;
        try { resp = await apiCustom.get<ApiResponse>(url); }
        catch (error: any) { if (version === requestVersion.current) setLoadError(error?.message || "Не удалось загрузить расписание"); return; }
        if (version !== requestVersion.current) return;
        const activeIndex = resp.monthArrayName.findIndex((month) => month.apiDate === resp.activeMonth?.apiDate);
        if (activeIndex < 0) {
            setLoadError("Сервер вернул несогласованный список месяцев");
            return;
        }
        setData(resp);
        setActive(activeIndex);
    };

    useEffect(() => {
        if (!activeCompanyResolved || !activeCompanyApiResolved || !activeCompanyId || !activeCompanyIdApi) {
            setData(null);
            return;
        }

        setData(null); setSelectedCells([]); setEditModal(null); setTransferModal(null); initialApplied.current = "";
        loadSchedule(initialDate?.slice(0, 7));
        const versionCounter = requestVersion;
        return () => { versionCounter.current++; };
    }, [activeCompanyApiResolved, activeCompanyId, activeCompanyIdApi, activeCompanyResolved, initialDate]);

    useEffect(() => {
        if (!data || !initialStaffId || !initialDate) return;
        const key = `${initialStaffId}:${initialDate}`;
        if (initialApplied.current === key) return;
        initialApplied.current = key;
        const index = data.monthArrayName.findIndex(month => month.apiDate === initialDate.slice(0, 7));
        if (index >= 0) setActive(index);
        if (data.staffSchedule.some(staff => staff.staffId === initialStaffId)) {
            setSelectedCells([{ staffId: initialStaffId, date: Number(initialDate.slice(8, 10)) }]);
        }
    }, [data, initialStaffId, initialDate]);

    useEffect(() => {
        if (todayRef.current) {
            todayRef.current.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
        }
    }, [data, active]);

    const paginate = async (newIndex: number) => {
        if (!data || navigationBusy.current || newIndex < 0 || newIndex >= data.monthArrayName.length || newIndex === active) return;
        navigationBusy.current = true;
        setMonthLoading(true);
        const monthApiDate = data.monthArrayName[newIndex].apiDate;
        setSelectedCells([]);
        setEditModal(null);
        setTransferModal(null);
        try {
            await loadSchedule(monthApiDate);
        } finally {
            navigationBusy.current = false;
            setMonthLoading(false);
        }
    };

    const formatTransferDateTime = (value?: string | null) => {
        if (!value) return "Дата не указана";
        const normalized = value.includes("T") ? value : value.replace(" ", "T");
        const date = new Date(normalized);
        if (Number.isNaN(date.getTime())) return value;
        return new Intl.DateTimeFormat("ru-RU", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        }).format(date);
    };

    const getInitialPeriodsForEdit = (cells: SelectedCell): Period[] => {
        if (!data || cells.length !== 1) {
            return [];
        }

        const [cell] = cells;
        const staffRow = data.staffSchedule.find((staff) => staff.staffId === cell.staffId);
        const periods = staffRow?.day[cell.date - 1] ?? [];

        return periods.map((period) => ({
            from: period.from,
            to: period.to,
            online: period.online,
        }));
    };

    const openEditModal = () => {
        setEditModal({
            open: true,
            cells: selectedCells.map((cell) => ({ ...cell })),
            periods: getInitialPeriodsForEdit(selectedCells),
        });
    };

    const saveSchedule = async (
        source: EditModalState,
        transferStaffId?: number
    ) => {
        try {
            await apiCustom.post("/lkfr/staff/save-schedule", {
                cells: source.cells,
                periods: source.periods,
                month: currentMonth.apiDate,
                ...(transferStaffId ? { transferStaffId } : {}),
            });
            setTransferModal(null);
            setEditModal(null);
            setSelectedCells([]);
            await loadSchedule(currentMonth.apiDate);
            onSaved?.();
        } catch (error) {
            const details = (error as any)?.details;
            const code = details?.code;

            if (code === "SHIFT_TRANSFER_REQUIRED") {
                const options: TransferStaffOption[] = Array.isArray(details?.staffOptions)
                    ? details.staffOptions
                    : [];
                setTransferModal({
                    open: true,
                    cells: source.cells,
                    periods: source.periods,
                    records: Array.isArray(details?.records) ? details.records : [],
                    staffOptions: options,
                    selectedStaffId: options[0]?.id ? String(options[0].id) : "",
                    message:
                        typeof details?.message === "string"
                            ? details.message
                            : "Есть будущие записи. Выберите мастера для переноса.",
                });
                return;
            }

            const message =
                typeof details?.message === "string"
                    ? details.message
                    : error instanceof Error
                        ? error.message
                        : "Не удалось сохранить расписание";
            window.alert(message);
        }
    };

    const swipeHandlers = useSwipeable({
        onSwipedLeft: (gesture) => {
            if (isIntentionalMonthSwipe(gesture) && data && active < data.monthArrayName.length - 1) paginate(active + 1);
        },
        onSwipedRight: (gesture) => {
            if (isIntentionalMonthSwipe(gesture) && active > 0) paginate(active - 1);
        },
        trackMouse: true,
        delta: 45,
    });

    if (!activeCompanyResolved || !activeCompanyApiResolved) return <div>Загрузка адресов...</div>;
    if (!activeCompanyId) return <div>Нет доступных точек.</div>;
    if (!activeCompanyIdApi) return <div>Для выбранной точки расписание пока не подключено.</div>;
    if (loadError) return <div role="alert">{loadError} <button type="button" onClick={() => loadSchedule(initialSelection?.date.slice(0, 7))}>Повторить</button></div>;
    if (!data) return <div>Загрузка...</div>;

    const currentMonth = data.monthArrayName[active];
    const days = data.dayMonth;
    const todayMoscow = moscowCalendarDate(new Date());

    const hasMoreRows = data.staffSchedule.length > VISIBLE_ROWS;
    const rows = collapsed && hasMoreRows ? data.staffSchedule.slice(0, VISIBLE_ROWS) : data.staffSchedule;

    return (
        <>
            <h2 className="section-title" id="timetable">Расписание мастеров</h2>

            <div className="card timetable-card" style={{ overflow: "hidden", position: "relative" }}>
                <div className="timetable-month-swipe-area" aria-busy={monthLoading}>
                    <div className="month-heading">
                        <button type="button" className="month-step" aria-label="Предыдущий месяц расписания"
                                disabled={monthLoading || active === 0} onClick={() => paginate(active - 1)}>‹</button>
                        <div className="timetable-month" {...swipeHandlers}>{currentMonth.alias}</div>
                        <button type="button" className="month-step" aria-label="Следующий месяц расписания"
                                disabled={monthLoading || active === data.monthArrayName.length - 1} onClick={() => paginate(active + 1)}>›</button>
                    </div>
                    {monthLoading && <div className="timetable-month-loading" role="status">Загрузка месяца…</div>}

                    <div
                        className="month-dots"
                        key={data.monthArrayName.map(m => m.apiDate).join("|")}
                    >
                        {data.monthArrayName.map((m, i) => (
                            <button type="button"
                                key={m.apiDate}
                                onClick={() => paginate(i)}
                                aria-label={m.alias}
                                aria-current={i === active ? "date" : undefined}
                                disabled={monthLoading}
                            />
                        ))}
                    </div>
                </div>

                <div
                    className={
                        `timetable-wrapper ${hasMoreRows ? "has-more" : ""} ${collapsed ? "is-collapsed" : ""}`
                    }
                >
                    <table className="timetable">
                        <thead>
                        <tr>
                            <th className="sticky-col">Мастер</th>
                            {days.map((label, i) => {
                                const [day, weekday] = label.split(" ");
                                const isToday =
                                    `${currentMonth.apiDate}-${String(parseInt(day, 10)).padStart(2, "0")}` === todayMoscow;

                                return (
                                    <th
                                        key={i}
                                        className={`${weekday === "Сб" || weekday === "Вс" ? "timetable-weekend" : ""} ${isToday ? "timetable-today" : ""}`}
                                        ref={isToday ? todayRef : null}
                                    >
                                        <div>{day}</div>
                                        <span>{weekday}</span>
                                    </th>
                                );
                            })}
                        </tr>
                        </thead>
                        <tbody>
                        {rows.map((m) => (
                            <tr key={m.staffId}>
                                <td className="sticky-col">{m.staffName}</td>
                                {m.day.map((periods, dayIdx) => {
                                    const isSelected = selectedCells.some(
                                        (cell) => cell.staffId === m.staffId && cell.date === dayIdx + 1
                                    );
                                    return (
                                        <td
                                            key={dayIdx}
                                            className={isSelected ? "cell-selected" : ""}
                                            onClick={() => {
                                                setSelectedCells((prev) => {
                                                    const already = prev.some(
                                                        (cell) => cell.staffId === m.staffId && cell.date === dayIdx + 1
                                                    );
                                                    return already
                                                        ? prev.filter(
                                                            (cell) =>
                                                                !(cell.staffId === m.staffId && cell.date === dayIdx + 1)
                                                        )
                                                        : [...prev, { staffId: m.staffId, date: dayIdx + 1 }];
                                                });
                                            }}
                                            style={{ cursor: "pointer", background: isSelected ? "#ffe06644" : undefined }}
                                        >
                                            {periods.length > 0 && (
                                                <div className="cell-time">
                                                    {periods.map((p, i) => (
                                                        <div key={i}>
                                                            {p.actualOpen ?? p.from} - {p.actualClose ?? p.to}
                                                            {p.online && (
                                                                <span style={{ color: "#2c204b", fontWeight: 600, fontSize: "0.87em", marginLeft: 4 }}>
                                                                    🟢 online
                                                                </span>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                        </tbody>
                    </table>

                    {/* Градиент при свернутом состоянии */}
                    {hasMoreRows && collapsed && <div className="timetable-fade" aria-hidden="true" />}
                </div>

                {/* Тогглер */}
                {hasMoreRows && (
                    <div className="timetable-collapse">
                        <button
                            type="button"
                            className={`collapse-toggle ${collapsed ? "" : "expanded"}`}
                            onClick={() => setCollapsed((v) => !v)}
                            aria-expanded={!collapsed}
                            aria-controls="timetable"
                        >
                            <span className="arrow" aria-hidden="true">▾</span>
                            {collapsed ? "Показать больше" : "Свернуть"}
                        </button>
                    </div>
                )}
            </div>

            {selectedCells.length > 0 && !editModal && (
                <div style={{ position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 1101 }} onClick={() => setSelectedCells([])}>
                    <div
                        className="timetable-modal-sheet"
                        style={{
                            left: 0, right: 0, bottom: 0, height: "22vh", background: "#fff",
                            borderTopLeftRadius: 20, borderTopRightRadius: 20,
                            boxShadow: "0 -2px 24px rgba(36,41,57,0.13)",
                            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                            zIndex: 1001, minHeight: 110, padding: "20px 14px", position: "relative",
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="timetable-modal-label">
                            {selectedCells.length === 1
                                ? `ID ${selectedCells[0].staffId}, день ${selectedCells[0].date}`
                                : `Выбрано ячеек: ${selectedCells.length}`}
                        </div>
                        <div style={{ display: "flex", gap: 14, marginTop: 14 }}>
                            <button
                                className="timetable-btn main"
                                onClick={openEditModal}
                            >
                                Изменить
                            </button>
                            <button className="timetable-btn" onClick={() => setSelectedCells([])}>Сброс</button>
                        </div>
                    </div>
                </div>
            )}

            {editModal && editModal.open && (
                <div className="timetable-modal-overlay" onClick={() => setEditModal(null)}>
                    <div className="timetable-modal-sheet timetable-modal-sheet-large timetable-modal-sheet-modern" onClick={(e) => e.stopPropagation()}>
                        <div className="timetable-modal-title">
                            {editModal.cells.length === 1
                                ? `ID ${editModal.cells[0].staffId}, день ${editModal.cells[0].date}`
                                : `Для ${editModal.cells.length} ячеек`}
                        </div>
                        <div className="timetable-periods-list">
                            {editModal.periods.map((period, idx) => (
                                <div key={idx} className="timetable-period-card">
                                    <div className="timetable-input-group">
                                        <label>
                                            <span>С</span>
                                            <input
                                                type="time"
                                                value={period.from}
                                                onChange={(e) =>
                                                    setEditModal((em) => em && ({
                                                        ...em,
                                                        periods: em.periods.map((p, i) => (i === idx ? { ...p, from: e.target.value } : p)),
                                                    }))
                                                }
                                            />
                                        </label>
                                        <span style={{ margin: "0 8px", fontWeight: 700 }}>–</span>
                                        <label>
                                            <span>До</span>
                                            <input
                                                type="time"
                                                value={period.to}
                                                onChange={(e) =>
                                                    setEditModal((em) => em && ({
                                                        ...em,
                                                        periods: em.periods.map((p, i) => (i === idx ? { ...p, to: e.target.value } : p)),
                                                    }))
                                                }
                                            />
                                        </label>
                                    </div>
                                    <div className="timetable-input-group timetable-checkbox-row">
                                        <label className="modern-checkbox">
                                            <input
                                                type="checkbox"
                                                checked={period.online}
                                                onChange={(e) =>
                                                    setEditModal((em) => em && ({
                                                        ...em,
                                                        periods: em.periods.map((p, i) => (i === idx ? { ...p, online: e.target.checked } : p)),
                                                    }))
                                                }
                                            />
                                            <span>Онлайн-запись</span>
                                        </label>
                                        {editModal.periods.length > 1 && (
                                            <button
                                                className="timetable-remove-btn"
                                                onClick={() =>
                                                    setEditModal((em) => em && ({
                                                        ...em,
                                                        periods: em.periods.filter((_, i) => i !== idx),
                                                    }))
                                                }
                                                title="Удалить период"
                                                type="button"
                                            >
                                                ×
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <button
                            className="timetable-btn add-period"
                            onClick={() =>
                                setEditModal((em) => em && ({ ...em, periods: [...em.periods, { from: "09:00", to: "13:00", online: false }] }))
                            }
                        >
                            + Добавить период
                        </button>
                        {editModal.periods.length > 0 && (
                            <button
                                className="timetable-btn"
                                onClick={() =>
                                    setEditModal((em) => em && ({ ...em, periods: [] }))
                                }
                                type="button"
                            >
                                Очистить смену
                            </button>
                        )}
                        <button
                            className="timetable-btn save-btn"
                            onClick={() => {
                                if (!editModal) return;
                                saveSchedule(editModal);
                            }}
                        >
                            Сохранить
                        </button>
                    </div>
                </div>
            )}

            {transferModal && transferModal.open && (
                <div className="timetable-modal-overlay" onClick={() => setTransferModal(null)}>
                    <div
                        className="timetable-modal-sheet timetable-modal-sheet-large timetable-modal-sheet-modern"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="timetable-modal-title">Перенос записей</div>
                        <div
                            style={{
                                fontSize: 14,
                                color: "#5f6574",
                                lineHeight: 1.5,
                                marginBottom: 16,
                            }}
                        >
                            {transferModal.message}
                        </div>

                        <div className="section-field">
                            <div className="section-label">Перенести на мастера</div>
                            <select
                                className="section-select"
                                value={transferModal.selectedStaffId}
                                onChange={(e) =>
                                    setTransferModal((state) =>
                                        state
                                            ? { ...state, selectedStaffId: e.target.value }
                                            : state
                                    )
                                }
                            >
                                <option value="">Выберите мастера</option>
                                {transferModal.staffOptions.map((option) => (
                                    <option key={option.id} value={option.id}>
                                        {option.name || `Мастер #${option.id}`}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div
                            style={{
                                maxHeight: 220,
                                overflowY: "auto",
                                marginTop: 16,
                                display: "grid",
                                gap: 10,
                            }}
                        >
                            {transferModal.records.map((record) => (
                                <div
                                    key={record.id}
                                    style={{
                                        borderRadius: 14,
                                        background: "#f6f7fb",
                                        padding: "12px 14px",
                                    }}
                                >
                                    <div style={{ fontWeight: 700 }}>
                                        {record.clientName || "Клиент не указан"}
                                    </div>
                                    <div style={{ fontSize: 13, color: "#5f6574", marginTop: 4 }}>
                                        {formatTransferDateTime(record.dateTime)}
                                    </div>
                                    <div style={{ fontSize: 13, color: "#5f6574", marginTop: 4 }}>
                                        {record.services?.length
                                            ? `Услуги: ${record.services.join(", ")}`
                                            : "Услуги не указаны"}
                                    </div>
                                    <div style={{ fontSize: 13, color: "#5f6574", marginTop: 4 }}>
                                        Сумма: {Number(record.amount || 0).toLocaleString("ru-RU")} ₽
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="section-actions" style={{ marginTop: 18 }}>
                            <button
                                type="button"
                                className="section-btn"
                                onClick={() => {
                                    if (!transferModal.selectedStaffId) {
                                        window.alert("Выберите мастера для переноса записей.");
                                        return;
                                    }
                                    saveSchedule(
                                        {
                                            open: true,
                                            cells: transferModal.cells,
                                            periods: transferModal.periods,
                                        },
                                        Number(transferModal.selectedStaffId)
                                    );
                                }}
                            >
                                Перенести и сохранить
                            </button>
                            <button
                                type="button"
                                className="section-btn-secondary"
                                onClick={() => setTransferModal(null)}
                            >
                                Отмена
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export const Timetable: React.FC<{ planning?: boolean; initialSelection?: { staffId: number; date: string }; onSaved?: () => void }> = ({ planning, ...props }) => planning ? <MonthPlanner /> : <CompactTimetable {...props} />;
